import { FunctionComponent, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { issueHref } from "./focusTarget";
import { issueText } from "./issueText";
import { ScoreIssue } from "./scoreIssues";

interface IssuePanelProps {
	anchorEl: HTMLElement;
	issues: ScoreIssue[];
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	/** Portal target; the Chrome extension passes its shadow container (web rule §11). */
	portalContainer?: HTMLElement | null;
}

const PANEL_WIDTH = 400;

/**
 * The hover list under a tab with issues (PREC-4055): every issue as a row, and
 * a click on a row jumps to the page and entity it names.
 */
export const IssuePanel: FunctionComponent<IssuePanelProps> = ({
	anchorEl,
	issues,
	onMouseEnter,
	onMouseLeave,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

	useLayoutEffect(() => {
		const update = () => {
			const rect = anchorEl.getBoundingClientRect();
			const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8));

			setPosition({ top: rect.bottom + 4, left });
		};

		update();
		window.addEventListener("scroll", update, true);
		window.addEventListener("resize", update);

		return () => {
			window.removeEventListener("scroll", update, true);
			window.removeEventListener("resize", update);
		};
	}, [anchorEl]);

	if (!position) {
		return null;
	}

	return createPortal(
		<div
			className="fixed z-[1600] overflow-hidden rounded-lg border border-border-light bg-white shadow-lg"
			data-testid="score-issue-panel"
			style={{ top: position.top, left: position.left, width: PANEL_WIDTH }}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<div className="border-b border-border-light px-3 py-2">
				<div className="text-sm font-semibold text-text">
					{t("leadScoring.shell.issuesToFix", { count: issues.length })}
				</div>
				<div className="text-xs text-text-meta">{t("leadScoring.shell.clickToJump")}</div>
			</div>
			<div className="max-h-[300px] overflow-auto p-1.5">
				{issues.map((issue, index) => (
					<button
						key={`${issue.kind}-${issue.id}-${issue.personaId ?? ""}-${index}`}
						className="flex w-full cursor-pointer items-start gap-2 rounded-md px-1.5 py-1.5 text-left text-xs text-status-error hover:bg-status-error-light"
						type="button"
						onClick={() => navigate(issueHref(issue))}
					>
						<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faTriangleExclamation} />
						<span>{issueText(t, issue)}</span>
					</button>
				))}
			</div>
		</div>,
		portalContainer ?? document.body
	);
};
